/*
 * ============================================================================
 * <socken-show> — Scroll-Choreografie der Produktanimation
 * ============================================================================
 *
 * Rechnet aus der Scrollposition einen Fortschritt p (0 = Bühne kommt oben an,
 * 1 = Bühne wird losgelassen) und daraus je Ebene einen eigenen, geglätteten
 * Wert. Die Ebenen selbst bewegt das Stylesheet über diese CSS-Variablen:
 *
 *   --p          Gesamtfortschritt (Bündchen-Band wandert)
 *   --rein       Sockenpaar fliegt ein                0.00 – 0.18
 *   --schwein    Aufkleber springt hervor (federnd)   0.14 – 0.30
 *   --banderole  Banderole rutscht herein             0.22 – 0.38
 *   --raus       Preis-Aufkleber und Schluss           0.86 – 0.97
 *
 * Dazwischen (0.30 – 0.86) der Rundgang: Der Bereich wird gleichmäßig auf die
 * Punkte verteilt; der jeweils aktive Punkt und sein Text bekommen .is-aktiv.
 *
 * Nur transform/opacity werden bewegt, je Bildschirmbild höchstens ein
 * requestAnimationFrame, und gerechnet wird nur, solange die Section im Bild
 * ist. Bei „weniger Bewegung“ passiert gar nichts: Ohne die Klasse .is-bewegt
 * zeigt das Stylesheet die ruhige Fassung mit allen Texten untereinander.
 * ============================================================================
 */
(() => {
  if (customElements.get('socken-show')) return;

  const PHASEN = {
    rein: [0, 0.18],
    schwein: [0.14, 0.3],
    banderole: [0.22, 0.38],
    raus: [0.86, 0.97],
  };
  const RUNDGANG = [0.3, 0.86];

  const klemmen = (x) => Math.min(1, Math.max(0, x));
  const sanft = (t) => 1 - Math.pow(1 - t, 3);
  // Leichtes Überschwingen — der Aufkleber „ploppt“ heraus statt zu gleiten.
  const federnd = (t) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  };

  class SockenShow extends HTMLElement {
    connectedCallback() {
      const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (ruhig.matches) return;

      this.classList.add('is-bewegt');
      this.punkte = [...this.querySelectorAll('.socken-show__punkt')];
      this.texte = [...this.querySelectorAll('.socken-show__text')];
      this.schritt = -1;

      this.beimScrollen = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.aktualisieren();
        });
      };

      this.beobachter = new IntersectionObserver(([eintrag]) => {
        if (eintrag.isIntersecting) {
          window.addEventListener('scroll', this.beimScrollen, { passive: true });
          window.addEventListener('resize', this.beimScrollen);
          this.aktualisieren();
        } else {
          this.abmelden();
        }
      });
      this.beobachter.observe(this);
      this.aktualisieren();
    }

    disconnectedCallback() {
      this.abmelden();
      this.beobachter?.disconnect();
    }

    abmelden() {
      window.removeEventListener('scroll', this.beimScrollen);
      window.removeEventListener('resize', this.beimScrollen);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    aktualisieren() {
      // Die Bühne klebt nicht an der Fensterkante, sondern unter dem klebenden
      // Header (top: var(--sticky-area-height)). p läuft deshalb von „Bühne
      // rastet unter dem Header ein“ bis „Bühne wird losgelassen“ — gerechnet ab
      // der Fensterkante hinkte die Geschichte um die Headerhöhe hinterher und
      // war beim Loslassen erst bei 0,94.
      const buehne = this.firstElementChild;
      const oben = buehne ? parseFloat(getComputedStyle(buehne).top) || 0 : 0;
      const box = this.getBoundingClientRect();
      const weg = box.height - (buehne?.getBoundingClientRect().height || window.innerHeight);
      const p = weg > 0 ? klemmen((oben - box.top) / weg) : 1;

      const phase = ([von, bis], kurve = sanft) => kurve(klemmen((p - von) / (bis - von)));
      this.style.setProperty('--p', p.toFixed(4));
      this.style.setProperty('--rein', phase(PHASEN.rein).toFixed(4));
      this.style.setProperty('--schwein', phase(PHASEN.schwein, federnd).toFixed(4));
      this.style.setProperty('--banderole', phase(PHASEN.banderole).toFixed(4));
      this.style.setProperty('--raus', phase(PHASEN.raus, federnd).toFixed(4));

      // Rundgang: 0 = noch nicht dran, 1…n = aktiver Punkt, n+1 = vorbei.
      const n = this.texte.length;
      const [von, bis] = RUNDGANG;
      let schritt = 0;
      if (p >= bis) schritt = n + 1;
      else if (p >= von && n) schritt = Math.min(n, Math.floor(((p - von) / (bis - von)) * n) + 1);

      if (schritt !== this.schritt) {
        this.schritt = schritt;
        this.dataset.schritt = String(schritt);
        const aktiv = (el) => Number(el.dataset.schritt) === schritt;
        this.punkte.forEach((el) => el.classList.toggle('is-aktiv', aktiv(el)));
        this.texte.forEach((el) => el.classList.toggle('is-aktiv', aktiv(el)));
        this.toggleAttribute('data-schluss', schritt > n);
      }
    }
  }

  customElements.define('socken-show', SockenShow);
})();
